if (!customElements.get('shop-by-reels')) {
  class ShopByReels extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.slider = this.querySelector('slider-component');
      this.sliderList = this.slider?.querySelector('[id^="Slider-"]');
      this.nextButton = this.slider?.querySelector('button[name="next"]');
      this.slides = Array.from(this.querySelectorAll('[data-reel-slide]'));
      this.dialog = this.querySelector('[data-reel-dialog]');
      this.modalContent = this.querySelector('[data-reel-modal-content]');
      this.autoplayEnabled =
        this.dataset.autoplay !== 'false' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.autoplayDelay = Math.max(Number(this.dataset.autoplayDelay) || 5000, 3000);
      this.handleClick = this.onClick.bind(this);
      this.handleSubmit = this.onSubmit.bind(this);
      this.handleSlideChange = (event) => this.setActiveSlide(event.detail.currentElement);
      this.handleDialogClose = () => {
        this.cleanupModal();
        this.startAutoplay();
      };
      this.handleVisibility = () => {
        if (document.hidden) {
          this.modalContent?.querySelector('video')?.pause();
          this.stopAutoplay();
        } else {
          this.startAutoplay();
        }
      };
      this.handleAutoplayPause = () => this.stopAutoplay();
      this.handleAutoplayResume = () => this.startAutoplay();
      this.handleFocusOut = (event) => {
        if (!this.slider?.contains(event.relatedTarget)) this.startAutoplay();
      };

      this.addEventListener('click', this.handleClick);
      this.addEventListener('submit', this.handleSubmit, true);
      this.slider?.addEventListener('slideChanged', this.handleSlideChange);
      this.dialog?.addEventListener('close', this.handleDialogClose);
      document.addEventListener('visibilitychange', this.handleVisibility);
      this.setActiveSlide(this.slides[0]);
      this.setupAutoplay();

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.onCartUpdate(event));
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      this.removeEventListener('submit', this.handleSubmit, true);
      this.slider?.removeEventListener('slideChanged', this.handleSlideChange);
      this.dialog?.removeEventListener('close', this.handleDialogClose);
      document.removeEventListener('visibilitychange', this.handleVisibility);
      this.slider?.removeEventListener('mouseenter', this.handleAutoplayPause);
      this.slider?.removeEventListener('mouseleave', this.handleAutoplayResume);
      this.slider?.removeEventListener('focusin', this.handleAutoplayPause);
      this.slider?.removeEventListener('focusout', this.handleFocusOut);
      this.slider?.removeEventListener('touchstart', this.handleAutoplayPause);
      this.slider?.removeEventListener('touchend', this.handleAutoplayResume);
      this.stopAutoplay();
      this.autoplayObserver?.disconnect();
      this.unsubscribeCart?.();
      this.cleanupModal(false);
      this.initialized = false;
    }

    onClick(event) {
      const closeButton = event.target.closest('[data-reel-close]');
      if (closeButton) {
        this.closeModal();
        return;
      }

      const trigger = event.target.closest('[data-reel-open]');
      if (trigger && this.contains(trigger)) {
        this.openModal(trigger);
        return;
      }

      if (event.target === this.dialog) this.closeModal();
    }

    onSubmit(event) {
      const form = event.target.closest('form[action*="/cart/add"]');
      const productForm = form?.closest('product-form');
      if (!form || !productForm || !this.modalContent?.contains(productForm)) return;
      if (productForm.dataset.cartHandler !== 'gokwik' && window.rbUseGoKwikSliderCart !== true) return;

      const submitter = event.submitter || productForm.querySelector('[type="submit"]');
      if (submitter?.disabled || submitter?.getAttribute('aria-disabled') === 'true') return;

      this.closeModal(false);
    }

    setActiveSlide(slide) {
      if (!slide) return;
      this.slides.forEach((item) => item.classList.toggle('is-active', item === slide));
    }

    setupAutoplay() {
      if (!this.autoplayEnabled || !this.slider || !this.sliderList || !this.nextButton) return;

      this.slider.addEventListener('mouseenter', this.handleAutoplayPause);
      this.slider.addEventListener('mouseleave', this.handleAutoplayResume);
      this.slider.addEventListener('focusin', this.handleAutoplayPause);
      this.slider.addEventListener('focusout', this.handleFocusOut);
      this.slider.addEventListener('touchstart', this.handleAutoplayPause, { passive: true });
      this.slider.addEventListener('touchend', this.handleAutoplayResume, { passive: true });

      if ('IntersectionObserver' in window) {
        this.autoplayObserver = new IntersectionObserver(([entry]) => {
          this.autoplayVisible = entry.isIntersecting;
          entry.isIntersecting ? this.startAutoplay() : this.stopAutoplay();
        }, { threshold: 0.25 });
        this.autoplayObserver.observe(this.slider);
      } else {
        this.autoplayVisible = true;
        this.startAutoplay();
      }
    }

    startAutoplay() {
      this.stopAutoplay();
      if (!this.autoplayEnabled || !this.autoplayVisible || document.hidden || this.dialog?.open) return;
      this.autoplayTimer = window.setTimeout(() => this.advanceSlider(), this.autoplayDelay);
    }

    stopAutoplay() {
      window.clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    advanceSlider() {
      if (this.sliderList.scrollWidth > this.sliderList.clientWidth + 1) {
        if (this.nextButton.disabled) {
          this.sliderList.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
          this.nextButton.click();
        }
      }
      this.startAutoplay();
    }

    openModal(trigger) {
      if (!this.dialog || !this.modalContent) return;
      const template = document.getElementById(trigger.dataset.templateId);
      if (!template || !this.contains(template)) return;

      this.activeTrigger = trigger;
      this.stopAutoplay();
      this.modalContent.replaceChildren(template.content.cloneNode(true));
      this.bodyWasLocked = document.body.classList.contains('overflow-hidden');
      this.bodyLockApplied = true;
      document.body.classList.add('overflow-hidden');

      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }

      this.startVideo();
    }

    startVideo() {
      const video = this.modalContent?.querySelector('video');
      const media = video?.closest('[data-reel-modal-media]');
      if (!video || !media) return;

      const setReady = () => media.classList.add('is-ready');
      const setWaiting = () => media.classList.remove('is-ready');
      video.addEventListener('canplay', setReady, { once: true });
      video.addEventListener('playing', setReady);
      video.addEventListener('waiting', setWaiting);
      video.addEventListener('stalled', setWaiting);
      video.addEventListener('error', setReady, { once: true });
      video.preload = 'auto';
      video.load();

      if (video.readyState >= video.HAVE_FUTURE_DATA) setReady();
      video.play().catch(setReady);
    }

    closeModal(restoreFocus = true) {
      if (!this.dialog?.hasAttribute('open')) return;
      this.restoreFocus = restoreFocus;

      if (typeof this.dialog.close === 'function') {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
        this.cleanupModal();
      }
    }

    cleanupModal(restoreFocus = this.restoreFocus !== false) {
      const video = this.modalContent?.querySelector('video');
      video?.pause();
      this.modalContent?.replaceChildren();

      if (this.bodyLockApplied && !this.bodyWasLocked) document.body.classList.remove('overflow-hidden');
      if (restoreFocus && this.activeTrigger?.isConnected) this.activeTrigger.focus();

      this.activeTrigger = null;
      this.restoreFocus = undefined;
      this.bodyWasLocked = false;
      this.bodyLockApplied = false;
    }

    onCartUpdate(event) {
      if (!this.dialog?.hasAttribute('open') || event.source !== 'product-form') return;
      const openVariant = this.modalContent?.querySelector('[name="id"]')?.value;
      if (String(event.productVariantId) !== String(openVariant)) return;

      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (cart?.setActiveElement && this.activeTrigger) cart.setActiveElement(this.activeTrigger);
      this.closeModal();
    }
  }

  customElements.define('shop-by-reels', ShopByReels);
}
